import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import Toast from './Toast';
import { useNavigate, useLocation } from 'react-router-dom';

const QuizDisplay = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState('topic');
  const [topic, setTopic] = useState('');
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quizSettings, setQuizSettings] = useState({
    difficulty: ['easy'],
    numQuestions: 5,
    questionTypes: ['mcq']
  });
  const [expandedExplanations, setExpandedExplanations] = useState({});
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [popularTopics, setPopularTopics] = useState([]);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [subtopics, setSubtopics] = useState([]);
  const [isLoadingSubtopics, setIsLoadingSubtopics] = useState(false);
  const [lastQuizDifficulty, setLastQuizDifficulty] = useState('');
  const [showAllSubtopics, setShowAllSubtopics] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [difficulty, setDifficulty] = useState('easy');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  
  // Calculate visible subtopics
  const visibleSubtopics = showAllSubtopics ? subtopics : subtopics.slice(0, 10);
  
  // Update topics per page based on screen size
  const topicsPerPage = isMobile ? 20 : 40;

  // Add resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update useEffect for fetching subtopics
  useEffect(() => {
    // Fetch subtopics when entering summary phase and we have a topic
    if (phase === 'summary' && topic && topic.trim()) {
      console.log('Fetching subtopics for topic:', topic); // For debugging
      fetchSubtopics(topic);
    }
  }, [phase, topic]);

  // Add this useEffect to handle initial subtopics fetch
  useEffect(() => {
    // Clear subtopics when leaving summary phase
    if (phase !== 'summary') {
      setSubtopics([]);
      setShowAllSubtopics(false);
    }
  }, [phase]);

  // Add a ref for the explanation content
  const explanationRef = useRef(null);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      if (phase === 'customize') {
        setPhase('topic');
      } else if (phase === 'quiz' || phase === 'review') {
        setPhase('customize');
      } else if (phase === 'summary') {
        setPhase('review');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [phase]);

  // Update URL when phase changes
  useEffect(() => {
    if (phase !== 'topic') {
      window.history.pushState({ phase }, '', `/quiz?phase=${phase}`);
    }
  }, [phase]);

  // Restore phase from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlPhase = params.get('phase');
    if (urlPhase && ['customize', 'quiz', 'review', 'summary'].includes(urlPhase)) {
      setPhase(urlPhase);
    }
  }, [location]);

  // Shuffle array utility
  const shuffleArray = (array) => {
    return array
      .map((value) => ({ value, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ value }) => value);
  };

  // Fetch popular topics when component mounts
  useEffect(() => {
    const fetchPopularTopics = async () => {
      try {
        setIsLoadingTopics(true);
        const response = await fetch('https://quizcracker-backend.vercel.app/quiz-topics');
        const data = await response.json();
        // Set topics directly without shuffling
        setPopularTopics(data.topics || []);
      } catch (error) {
        console.error('Error fetching popular topics:', error);
      } finally {
        setIsLoadingTopics(false);
      }
    };

    fetchPopularTopics();
  }, []);

  const handleTopicClick = (selectedTopic) => {
    setTopic(selectedTopic);
    setPhase('customize');
  };

  const handleShowMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handleShowLess = () => {
    setCurrentPage(1);
  };

  // Calculate visible topics based on screen size
  const initialVisibleCount = isMobile ? 20 : 40;
  const visibleTopics = popularTopics.slice(0, currentPage * initialVisibleCount);

  const goToFeedback = () => {
    navigate('/feedback');
  };

  const handleTopicSubmit = () => {
    if (!topic.trim()) {
      setError('Please enter a topic');
      return;
    }
    setPhase('customize');
  };

  const handleQuizGeneration = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setPhase('generating');

      // Map the UI quiz type to API expected format
      const questionType = quizSettings.questionTypes[0] === 'boolean' ? 'true_false' : 'mcq';

      const payload = {
        topic: topic,
        subtopics: ["Core Concepts"],
        difficulty: quizSettings.difficulty,
        numQuestions: quizSettings.numQuestions,
        questionTypes: [questionType]
      };

      console.log('Sending payload for quiz generation:', payload); // For debugging

      const response = await fetch('https://quizcracker-backend.vercel.app/get-quiz', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate quiz. Please try again.');
      }

      if (!data.quiz || !data.quiz.questions || !Array.isArray(data.quiz.questions) || data.quiz.questions.length === 0) {
        throw new Error('No questions were generated. Please try a different topic or settings.');
      }

      // Store the current difficulty for reference
      setLastQuizDifficulty(quizSettings.difficulty[0]);
      
      setQuestions(data.quiz.questions);
      setCurrentQuestionIndex(0);
      setSelectedAnswers({});
      setScore(0);
      setPhase('quiz');
    } catch (err) {
      console.error('Quiz generation error:', err);
      setError(err.message || 'Failed to generate quiz. Please try again.');
      setPhase('customize');
    } finally {
      setIsLoading(false);
    }
  };

  // New function to directly generate a quiz with a specific difficulty
  const generateQuizWithDifficulty = async (newDifficulty) => {
    try {
      setIsLoading(true);
      setError(null);
      setPhase('generating');

      // Map the UI quiz type to API expected format
      const questionType = quizSettings.questionTypes[0] === 'boolean' ? 'true_false' : 'mcq';

      const payload = {
        topic: topic,
        subtopics: ["Core Concepts"],
        difficulty: [newDifficulty], // Directly use the new difficulty
        numQuestions: quizSettings.numQuestions,
        questionTypes: [questionType]
      };

      console.log('Generating quiz with new difficulty:', newDifficulty);
      console.log('Sending payload:', payload);

      const response = await fetch('https://quizcracker-backend.vercel.app/get-quiz', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate quiz. Please try again.');
      }

      if (!data.quiz || !data.quiz.questions || !Array.isArray(data.quiz.questions) || data.quiz.questions.length === 0) {
        throw new Error('No questions were generated. Please try a different topic or settings.');
      }

      // Update quiz settings with the new difficulty
      setQuizSettings(prev => ({
        ...prev,
        difficulty: [newDifficulty]
      }));
      
      // Store the current difficulty for reference
      setLastQuizDifficulty(newDifficulty);
      
      setQuestions(data.quiz.questions);
      setCurrentQuestionIndex(0);
      setSelectedAnswers({});
      setScore(0);
      setPhase('quiz');
    } catch (err) {
      console.error('Quiz generation error:', err);
      setError(err.message || 'Failed to generate quiz. Please try again.');
      setPhase('customize');
    } finally {
      setIsLoading(false);
    }
  };

  const renderTopicPhase = () => {
    // Calculate visible topics based on screen size
    const initialVisibleCount = isMobile ? 20 : 40;
    const visibleTopics = popularTopics.slice(0, currentPage * initialVisibleCount);

    return (
    <div className="space-y-8 animate-fadeIn">
      {/* Create Your Quiz Section - More Prominent */}
      <div className="bg-white rounded-2xl shadow-md p-4 sm:p-8 border border-gray-100">
        <div className="text-center mb-4 sm:mb-8">
          <h2 className="text-4xl font-extrabold text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis">Create Your Quiz</h2>
          <p className="text-base sm:text-lg text-gray-600 mt-2">Start by entering any topic</p>
        </div>
        
        <div className="max-w-2xl mx-auto">
          <div className="relative mb-4">
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading && topic.trim()) {
                  handleTopicSubmit();
                }
              }}
              placeholder="e.g., World History, Solar System..."
              className="w-full px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg rounded-full border-2 border-gray-200 shadow-[0_1px_6px_rgba(32,33,36,.28)] hover:shadow-[0_1px_6px_rgba(32,33,36,.38)] focus:shadow-[0_1px_6px_rgba(32,33,36,.38)] focus:border-indigo-300 transition-all duration-200 outline-none"
            />
          </div>
          
          {/* Mobile Next Button */}
          <div className="sm:hidden">
            <button
              onClick={handleTopicSubmit}
              disabled={isLoading || !topic.trim()}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 
                       disabled:bg-indigo-300 transition-all duration-200 text-base font-medium 
                       shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              <span>Next</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Desktop Next Button */}
          <div className="hidden sm:flex justify-center">
            <button
              onClick={handleTopicSubmit}
              disabled={isLoading || !topic.trim()}
              className="w-[320px] bg-indigo-600 text-white py-4 rounded-lg hover:bg-indigo-700 
                       disabled:bg-indigo-300 transition-all duration-200 text-lg font-medium 
                       shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span>Loading...</span>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </>
              ) : (
                <>
                  <span>Next</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Popular Topics Section - Tag-like design */}
      <div className="max-w-4xl mx-auto sm:px-4">
        <div className="text-center mb-2">
          <h3 className="text-xl font-medium text-gray-800 mb-1">User-Created Quizzes</h3>
          <p className="text-xs text-gray-500">Click to start a quiz</p>
        </div>
        
        {isLoadingTopics ? (
          <div className="flex justify-center items-center py-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5 px-2">
              {visibleTopics.map((popularTopic, index) => (
                <button
                  key={index}
                  onClick={() => handleTopicClick(popularTopic)}
                  className="px-2.5 py-0.5 bg-white rounded-full border border-gray-200 
                           hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-200 
                           text-gray-700 hover:text-indigo-600 text-sm
                           shadow-sm hover:shadow active:bg-indigo-100 whitespace-nowrap
                           sm:max-w-none max-w-[calc(100vw-2rem)] overflow-hidden"
                  title={popularTopic}
                >
                  <span className="block truncate">{popularTopic}</span>
                </button>
              ))}
            </div>
            
            {popularTopics.length > visibleTopics.length && (
              <div className="flex justify-center mt-2">
                <button
                  onClick={handleShowMore}
                  className="text-xs text-indigo-600 hover:text-indigo-800 
                           flex items-center gap-1 px-2 py-1 rounded-full
                           hover:bg-indigo-50 transition-all duration-200"
                >
                  Show More
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            )}

            <div className="text-center text-xs text-gray-400">
              Showing {visibleTopics.length} of {popularTopics.length} topics
            </div>
          </div>
        )}
      </div>
    </div>
  );
  };

  const renderCustomizePhase = () => {
    return (
    <div className="space-y-8 sm:space-y-12 animate-fadeIn">
      {/* Topic Header Section */}
      <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-indigo-600 mb-1">Selected Topic</div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words pr-4">{topic}</h1>
          </div>
          <button
            onClick={() => setPhase('topic')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 
                     bg-white border border-gray-300 rounded-lg hover:bg-gray-50 
                     hover:border-gray-400 transition-all duration-200 group"
          >
            <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Edit Topic
          </button>
        </div>
      </div>

      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-4">Quiz Options</h2>
        <p className="text-gray-600">Choose your quiz preferences below to generate a customized quiz</p>
      </div>

      <div className="space-y-8">
          {/* Question Type Section */}
          <div className="space-y-4 bg-white p-6 sm:p-8 rounded-lg shadow-sm">
            <label className="block text-base font-medium text-gray-700 mb-4">
              Question Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'mcq', label: 'Multiple Choice', icon: '☑️' },
                { id: 'boolean', label: 'True/False', icon: '⚖️' }
              ].map((type) => (
                <div
                  key={type.id}
                  className={`relative rounded-lg border cursor-pointer transition-all duration-200 p-4
                    ${quizSettings.questionTypes.includes(type.id)
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300'}`}
                  onClick={() => setQuizSettings(prev => ({
                    ...prev,
                    questionTypes: [type.id]
                  }))}
                >
                  <div className="flex flex-col items-center space-y-2">
                    <span className="text-2xl">{type.icon}</span>
                    <span className="text-sm font-medium text-gray-900">{type.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Difficulty Level Section */}
          <div className="space-y-4 bg-white p-6 sm:p-8 rounded-lg shadow-sm">
            <label className="block text-base font-medium text-gray-700 mb-4">
              Select Difficulty Level
            </label>
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              {['easy', 'medium', 'hard'].map((level) => (
                <div
                  key={level}
                  className={`relative rounded-lg border cursor-pointer transition-all duration-200
                    ${quizSettings.difficulty[0] === level 
                      ? 'border-indigo-600 bg-indigo-50' 
                      : 'border-gray-200 hover:border-indigo-300'}`}
                  onClick={() => setQuizSettings(prev => ({ 
                    ...prev, 
                    difficulty: [level] 
                  }))}
                >
                  <div className="flex items-center justify-between p-2 sm:p-6">
                    <div className="text-sm sm:text-base font-medium text-gray-900 capitalize">
                      {level}
                    </div>
                    <div className={`ml-1 h-4 w-4 sm:h-5 sm:w-5 rounded-full border flex items-center justify-center flex-shrink-0
                      ${quizSettings.difficulty[0] === level 
                        ? 'border-indigo-600 bg-indigo-600' 
                        : 'border-gray-300'}`}
                    >
                      {quizSettings.difficulty[0] === level && (
                        <div className="h-2 w-2 sm:h-3 sm:w-3 rounded-full bg-white" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Add a note about the current difficulty */}
            <div className="mt-2 text-sm text-indigo-600 font-medium flex items-center">
              {lastQuizDifficulty ? (
                <>
                  <svg className="w-4 h-4 mr-1.5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Your last quiz was <span className="font-bold ml-1">{lastQuizDifficulty}</span> <span>difficulty</span>. Select a different level to change.
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1.5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Select a difficulty level for your quiz.
                </>
              )}
            </div>
          </div>

          {/* Number of Questions Section */}
          <div className="space-y-4 bg-white p-6 sm:p-8 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <label className="block text-base font-medium text-gray-700">
                Number of Questions
              </label>
            </div>
            <select
              value={quizSettings.numQuestions}
              onChange={(e) => setQuizSettings(prev => ({ 
                ...prev, 
                numQuestions: parseInt(e.target.value) 
              }))}
              className="w-full p-3 border rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {[...Array(10)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                    {i + 1} 
                </option>
              ))}
            </select>
        </div>

        {/* Generate Quiz Button */}
        <button
          onClick={handleQuizGeneration}
          className="w-full bg-indigo-600 text-white py-4 rounded-lg hover:bg-indigo-700 
                   transition-all duration-200 text-lg font-medium"
        >
          Generate Quiz →
        </button>
      </div>
    </div>
  );
  };

  const renderQuizPhase = () => {
    const currentQuestion = questions[currentQuestionIndex];
    
    if (!currentQuestion) return null;

    // Extract text and options safely
    const questionText = typeof currentQuestion.text === 'string' 
      ? currentQuestion.text 
      : (currentQuestion.question || "Question text unavailable");
    
    const options = Array.isArray(currentQuestion.options) 
      ? currentQuestion.options 
      : [];

    const explanation = currentQuestion.explanation || "";
    const userAnswer = selectedAnswers[currentQuestion.id];
    const isReview = phase === 'review';
    const isLastQuestion = currentQuestionIndex === questions.length - 1;
    const isCorrect = userAnswer === currentQuestion.answer;

    const handleAnswerSelect = (optionId) => {
      if (!isReview) {
        setSelectedAnswers(prev => ({
          ...prev,
          [currentQuestion.id]: optionId
        }));
      }
    };

    const handleBackToResults = () => {
      setPhase('summary');
      window.scrollTo(0, 0);
    };

    const calculateQuizScore = () => {
      let correctAnswers = 0;
      questions.forEach(question => {
        if (selectedAnswers[question.id] === question.answer) {
          correctAnswers++;
        }
      });
      const percentage = Math.round((correctAnswers / questions.length) * 100);
      setScore(percentage);
    };

    const handleNext = () => {
      if (currentQuestionIndex === questions.length - 1) {
        if (!isReview) {
          calculateQuizScore();
          setPhase('summary');
        } else {
          setPhase('topic');
          setTopic('');
          setSelectedAnswers({});
          setScore(0);
          setCurrentQuestionIndex(0);
        }
      } else {
        setCurrentQuestionIndex(prev => prev + 1);
        // Scroll to top when moving to next question
        window.scrollTo(0, 0);
      }
    };

    const getOptionStyle = (optionId, isReview, userAnswer, correctAnswer) => {
      if (isReview) {
        if (optionId === correctAnswer) return 'border-green-500 bg-green-500 text-white';
        if (optionId === userAnswer && optionId !== correctAnswer) return 'border-red-500 bg-red-500 text-white';
        return 'border-gray-300';
      }
      return userAnswer === optionId ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300';
    };

    const getOptionIcon = (optionId, isReview, userAnswer, correctAnswer) => {
      if (!isReview && userAnswer !== optionId) return null;
      if (isReview && optionId !== correctAnswer && optionId !== userAnswer) return null;
      
      if (isReview && optionId === correctAnswer) {
        return (
          <svg className="w-4 h-4 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" 
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
              clipRule="evenodd" 
            />
          </svg>
        );
      }
      
      if (isReview && optionId === userAnswer && optionId !== correctAnswer) {
        return (
          <svg className="w-4 h-4 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" 
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
              clipRule="evenodd" 
            />
          </svg>
        );
      }
      
      return (
        <svg className="w-4 h-4 text-white" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" 
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
            clipRule="evenodd" 
          />
        </svg>
      );
    };

    const getNavigationButtonStyle = () => {
      return currentQuestionIndex === questions.length - 1
        ? 'bg-green-600 hover:bg-green-700 active:bg-green-800 text-white focus:ring-green-500'
        : 'bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white focus:ring-indigo-500';
    };

    const getNavigationButtonText = () => {
      if (currentQuestionIndex === questions.length - 1) {
        return isReview ? 'Start New Quiz' : 'Finish Quiz';
      }
      return 'Next Question';
    };

    // Toggle explanation visibility and scroll to it
    const toggleExplanation = () => {
      const newExpandedState = !expandedExplanations[currentQuestion.id];
      
      setExpandedExplanations(prev => ({
        ...prev,
        [currentQuestion.id]: newExpandedState
      }));
      
      // If expanding, scroll to the explanation after a short delay to allow rendering
      if (newExpandedState) {
        setTimeout(() => {
          if (explanationRef.current) {
            explanationRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }
        }, 100);
      }
    };

    return (
      <div className="flex flex-col min-h-[calc(100vh-64px)]">
        {/* Quiz Progress Header - Balanced sizing */}
        <div className="sticky top-0 left-0 right-0 bg-white border-b z-30">
          <div className="max-w-3xl mx-auto px-3 sm:px-4">
            <div className="py-2.5 sm:py-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className="text-sm sm:text-lg font-semibold text-gray-900">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                  {isReview && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-800">
                      Review Mode
                    </span>
                  )}
                </div>
                {isReview && (
                  <button
                    onClick={handleBackToResults}
                    className="text-sm sm:text-base font-medium text-indigo-600 hover:text-indigo-700
                             transition-colors duration-200 flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Results
                  </button>
                )}
              </div>
              <div className="w-full h-1.5 sm:h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-indigo-600 transition-all duration-300 ease-out rounded-full"
                  style={{ width: `${(currentQuestionIndex + 1) / questions.length * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area - Balanced spacing */}
        <div className="flex-1 overflow-y-auto pt-3 sm:pt-4 pb-[72px] sm:pb-4 px-3 sm:px-4">
          <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
            {/* Question Card - Larger text and spacing */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-4 sm:p-8 border-b bg-gradient-to-br from-gray-50 to-white">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 leading-normal sm:leading-snug">
                  {questionText.includes('```') ? (
                    <>
                      {questionText.split('```').map((part, index) => {
                        if (index % 2 === 0) {
                          // Regular text
                          return <span key={index}>{part}</span>;
                        } else {
                          // Code block
                          const [language, ...codeParts] = part.split('\n');
                          const code = codeParts.join('\n').trim();
                          return (
                            <div key={index} className="my-4">
                              <div className="bg-gray-800 rounded-t-lg px-4 py-2 flex justify-between items-center">
                                <span className="text-xs text-gray-400 font-mono">{language}</span>
                                <div className="flex space-x-2">
                                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                </div>
                              </div>
                              <pre className="bg-gray-900 rounded-b-lg p-4 overflow-x-auto">
                                <code className="text-sm sm:text-base font-mono text-gray-200 whitespace-pre">
                                  {code}
                                </code>
                              </pre>
                            </div>
                          );
                        }
                      })}
                    </>
                  ) : (
                    questionText
                  )}
                </h2>
              </div>

              <div className="p-3 sm:p-8 space-y-3 sm:space-y-4">
                {options.map((option, index) => {
                  const optionId = typeof option === 'object' ? option.id : `option-${index}`;
                  const optionText = typeof option === 'object' ? option.text : option;
                  
                  const isSelected = userAnswer === optionId;
                  const isCorrect = isReview && optionId === currentQuestion.answer;
                  const isIncorrect = isReview && isSelected && optionId !== currentQuestion.answer;
                  
                  return (
                    <button
                      key={optionId}
                      onClick={() => handleAnswerSelect(optionId)}
                      disabled={isReview}
                      className={`w-full p-3.5 sm:p-5 text-left rounded-xl transition-all duration-200
                        ${isSelected && !isReview ? 'bg-indigo-50 border border-indigo-600 shadow-sm' : 'border border-gray-100'}
                        ${isCorrect ? 'bg-green-50 border border-green-600' : ''}
                        ${isIncorrect ? 'bg-red-50 border border-red-600' : ''}
                        ${!isSelected && !isReview ? 'hover:border-indigo-300 hover:bg-indigo-50/50 hover:shadow-sm' : ''}
                        group relative overflow-hidden`}
                    >
                      <div className="flex items-center space-x-3 sm:space-x-3 relative z-10">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 rounded-full border flex items-center justify-center
                          ${isSelected && !isReview ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300 group-hover:border-indigo-400'}
                          ${isCorrect ? 'border-green-600 bg-green-600' : ''}
                          ${isIncorrect ? 'border-red-600 bg-red-600' : ''}
                          transition-colors duration-200`}
                        >
                          {getOptionIcon(optionId, isReview, userAnswer, currentQuestion.answer)}
                        </div>
                        <span className={`flex-1 font-medium text-sm sm:text-base leading-normal sm:leading-snug
                          ${isSelected && !isReview ? 'text-indigo-900' : 'text-gray-700'}
                          ${isCorrect ? 'text-green-900' : ''}
                          ${isIncorrect ? 'text-red-900' : ''}
                          group-hover:text-indigo-900`}
                        >
                          {optionText}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mobile Explanation Toggle Button in Review Mode */}
            {isReview && explanation && (
              <div className="sm:hidden mt-4">
                <button
                  onClick={toggleExplanation}
                  className="w-full px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-lg
                           hover:bg-indigo-100 transition-colors duration-200 flex items-center 
                           justify-between text-indigo-700 font-medium"
                >
                  <span className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                      />
                    </svg>
                    View Explanation
                  </span>
                  <svg 
                    className={`w-5 h-5 transform transition-transform duration-200 
                              ${expandedExplanations[currentQuestion.id] ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            )}

            {/* Mobile Explanation Content in Review Mode */}
            {isReview && explanation && expandedExplanations[currentQuestion.id] && (
              <div className="sm:hidden mt-3">
                <div 
                  ref={explanationRef}
                  className="bg-white rounded-lg shadow-sm p-4 border border-indigo-200"
                >
                  <div className="prose prose-sm max-w-none text-gray-700">
                    {explanation.split('\n\n').map((point, index) => (
                      <div key={index} className="mb-3 last:mb-0">
                        {point.match(/^\d+\.|•|-/) ? (
                          <div className="flex items-start space-x-3">
                            <span className="text-indigo-600 font-medium">{point.match(/^\d+\.|•|-/)[0]}</span>
                            <p className="flex-1 leading-relaxed">
                              {point.replace(/^\d+\.|•|-/, '').trim()}
                            </p>
                          </div>
                        ) : (
                          <p className="leading-relaxed">
                            {point}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Desktop Explanation */}
            {isReview && explanation && (
              <div className="hidden sm:block bg-indigo-50 rounded-xl shadow-sm overflow-hidden border-2 border-indigo-200">
                <div className="p-5 sm:p-6 border-b border-indigo-200 bg-indigo-100">
                  <h2 className="text-xl sm:text-2xl font-semibold text-indigo-800 leading-relaxed flex items-center">
                    <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                      />
                    </svg>
                    Explanation
                  </h2>
                </div>
                <div className="p-5 sm:p-6 space-y-3">
                  {explanation.split('\n\n').map((point, index) => (
                    <div key={index} className="mb-3 last:mb-0">
                      {point.match(/^\d+\.|•|-/) ? (
                        <div className="flex items-start space-x-3">
                          <span className="text-indigo-600 font-medium">{point.match(/^\d+\.|•|-/)[0]}</span>
                          <p className="text-indigo-900/90 leading-relaxed flex-1">
                            {point.replace(/^\d+\.|•|-/, '').trim()}
                          </p>
                        </div>
                      ) : (
                        <p className="text-indigo-900/90 leading-relaxed">
                          {point}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Desktop Navigation */}
            <div className="hidden sm:block">
              <div className="flex items-center gap-3 mt-6">
                {currentQuestionIndex > 0 && (
                  <button
                    onClick={() => {
                      setCurrentQuestionIndex(prev => prev - 1);
                      window.scrollTo(0, 0);
                    }}
                    className="flex-1 sm:flex-initial px-4 py-3 bg-white border-2 border-gray-300 
                             rounded-lg transition-colors flex items-center justify-center gap-2
                             hover:bg-gray-50 active:bg-gray-100"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span className="font-medium">Previous</span>
                  </button>
                )}
                
                {isReview && isLastQuestion ? (
                  <button
                    onClick={handleBackToResults}
                    className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg
                             hover:bg-indigo-700 transition-colors flex items-center 
                             justify-center gap-2 font-medium"
                  >
                    <span>Back to Results</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    className={`flex-1 px-4 py-3 rounded-lg transition-colors flex items-center 
                             justify-center gap-2 font-medium ${getNavigationButtonStyle()}`}
                  >
                    <span>{getNavigationButtonText()}</span>
                    {currentQuestionIndex !== questions.length - 1 && (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
              
              {/* Desktop Feedback button in review mode */}
              {isReview && currentQuestionIndex === questions.length - 1 && (
                <div className="mt-4">
                  <button
                    onClick={goToFeedback}
                    className="w-full px-4 py-2 bg-white border border-indigo-300 text-indigo-600 
                             rounded-lg hover:bg-indigo-50 transition-colors duration-200 flex items-center 
                             justify-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" 
                      />
                    </svg>
                    Share Your Feedback
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation Footer - Larger buttons */}
        <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t z-30">
          <div className="max-w-3xl mx-auto p-3">
            <div className="flex items-center gap-2">
              {currentQuestionIndex > 0 && (
                <button
                  onClick={() => {
                    setCurrentQuestionIndex(prev => prev - 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="flex-none px-3 py-2.5 bg-white border border-gray-300 
                           rounded-lg transition-colors flex items-center justify-center gap-1.5
                           hover:bg-gray-50 active:bg-gray-100"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              
              {isReview && isLastQuestion ? (
                <button
                  onClick={handleBackToResults}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg
                           hover:bg-indigo-700 transition-colors flex items-center 
                           justify-center gap-2 font-medium"
                >
                  <span>Back to Results</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={() => {
                    handleNext();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`flex-1 px-4 py-3 rounded-lg transition-colors flex items-center 
                           justify-center gap-2 font-medium text-base ${getNavigationButtonStyle()}`}
                  disabled={!userAnswer && !isReview}
                >
                  <span>{getNavigationButtonText()}</span>
                  {currentQuestionIndex !== questions.length - 1 && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getPerformanceMessage = (score) => {
    if (score >= 90) return "Outstanding!";
    if (score >= 80) return "Excellent work!";
    if (score >= 70) return "Great job!";
    if (score >= 60) return "Good effort!";
    return "Keep practicing!";
  };

  const getNextDifficulty = (currentDifficulty) => {
    if (currentDifficulty === "easy") return "medium";
    if (currentDifficulty === "medium") return "hard";
    return currentDifficulty;
  };

  const fetchSubtopics = async (topic) => {
    setIsLoadingSubtopics(true);
    try {
      const response = await fetch(`https://quizcracker-backend.vercel.app/get-subtopics/?topic=${encodeURIComponent(topic)}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch subtopics');
      }
      
      const data = await response.json();
      console.log('Subtopics response:', data); // For debugging
      
      if (data && Array.isArray(data.subtopics)) {
        setSubtopics(data.subtopics);
        setShowAllSubtopics(false); // Reset show all state when fetching new subtopics
      } else {
        console.error('Invalid subtopics data format:', data);
        setSubtopics([]);
      }
    } catch (error) {
      console.error('Error fetching subtopics:', error);
      setSubtopics([]);
    } finally {
      setIsLoadingSubtopics(false);
    }
  };

  const handleRetakeQuiz = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const currentDifficulty = currentQuestion?.difficulty || 'easy';
    
    // Keep the last used settings but update difficulty
    const nextDifficulty = currentDifficulty === 'easy' ? 
      ['medium', 'hard'] :  // For easy, offer both medium and hard
      ['hard'];            // For medium, offer only hard
    
    // Maintain the last quiz settings
    setQuizSettings(prev => ({
      ...prev,
      difficulty: nextDifficulty
    }));
    
    handleQuizGeneration();
  };

  const renderDifficultyOptions = (currentDifficulty) => {
    if (currentDifficulty === 'easy') {
      return (
        <div className="flex flex-col space-y-3">
          <button
            onClick={() => {
              setQuizSettings(prev => ({ 
                ...prev, 
                difficulty: ['medium'],
                topic: topic // Keep the same topic
              }));
              handleQuizGeneration();
            }}
            className="inline-flex items-center px-4 py-2.5 bg-indigo-600 text-white rounded-lg
                     hover:bg-indigo-700 transition-colors duration-200 text-base"
          >
            <span className="flex-1">Try Medium Level Quiz on {topic}</span>
            <svg className="w-5 h-5 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
          <button
            onClick={() => {
              setQuizSettings(prev => ({ 
                ...prev, 
                difficulty: ['hard'],
                topic: topic // Keep the same topic
              }));
              handleQuizGeneration();
            }}
            className="inline-flex items-center px-4 py-2.5 bg-purple-600 text-white rounded-lg
                     hover:bg-purple-700 transition-colors duration-200 text-base"
          >
            <span className="flex-1">Challenge Yourself with Hard Level on {topic}</span>
            <svg className="w-5 h-5 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-2 bg-white text-sm text-gray-500">or</span>
            </div>
          </div>
          <button
            onClick={() => setPhase('topic')}
            className="inline-flex items-center px-4 py-2.5 bg-white border border-gray-300 
                     rounded-lg hover:bg-gray-50 transition-colors duration-200 text-base"
          >
            Create a New Quiz
          </button>
        </div>
      );
    }
    return (
      <button
        onClick={() => {
          setQuizSettings(prev => ({ ...prev, difficulty: ['hard'] }));
          handleQuizGeneration();
        }}
        className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg
                 hover:bg-purple-700 transition-colors duration-200"
      >
        Try Hard Level
        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </button>
    );
  };

  const renderSummaryPhase = () => {
    const performanceScore = Math.round(score);
    const isLowScore = performanceScore < 60;

    return (
      <div className="min-h-screen bg-gray-50 sm:py-6 sm:px-6">
        <div className="bg-white rounded-xl shadow-lg sm:p-8 p-4 max-w-3xl mx-auto">
          {/* Score Display and Header Section */}
          <div className="text-center mb-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-indigo-900 mb-2">
              {isLowScore ? "Let's Break It Down!" : getPerformanceMessage(performanceScore)}
            </h2>
            <div className="text-2xl font-semibold text-gray-600 mb-3">
              You scored {performanceScore}%
            </div>
            {isLowScore && (
              <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
                Don't worry! Let's review your answers and explore specific areas of {topic} to help you improve.
              </p>
            )}
          </div>

          {/* Review Answers Section */}
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl sm:p-8 p-4 shadow-md border border-indigo-200 mb-4 sm:mb-8">
            <div className="flex flex-col items-center space-y-3">
              <h3 className="text-xl font-semibold text-indigo-900">
                {performanceScore === 100 && lastQuizDifficulty === 'hard'
                  ? "Master the Details"
                  : isLowScore ? "See Where You Can Improve" : "Review Your Answers"}
              </h3>
              <p className="text-gray-600 text-center text-sm sm:text-base">
                {performanceScore === 100 && lastQuizDifficulty === 'hard'
                  ? "Perfect score! Review the explanations to solidify your understanding"
                  : "See detailed explanations and reinforce your understanding"}
              </p>
              <button
                onClick={() => setPhase('review')}
                className="w-full px-4 py-2.5 bg-indigo-600 text-white rounded-lg
                         hover:bg-indigo-700 transition-all duration-200 text-base font-medium
                         shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Review Answers
              </button>
            </div>
          </div>

          {/* Try Different Difficulty Section */}
          {!(performanceScore === 100 && lastQuizDifficulty === 'hard') && (
            <div className="bg-white rounded-xl sm:p-8 p-4 shadow-md border border-gray-200 mb-4 sm:mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Try Again on {topic}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
                {['easy', 'medium', 'hard'].map((level) => {
                  const isRecommended = 
                    (lastQuizDifficulty === 'easy' && level === 'medium' && performanceScore >= 80) ||
                    (lastQuizDifficulty === 'medium' && level === 'hard' && performanceScore >= 80);
                  const isCurrent = level === lastQuizDifficulty;
                  
                  return (
                    <button
                      key={level}
                      onClick={() => generateQuizWithDifficulty(level)}
                      className={`relative p-3 rounded-lg border transition-all duration-200
                        ${isCurrent 
                          ? 'border-indigo-300 bg-indigo-50/50 hover:bg-indigo-50 hover:border-indigo-400' 
                          : isRecommended
                            ? 'border-indigo-500 bg-indigo-50 hover:bg-indigo-100'
                            : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'}`}
                    >
                      <div className="text-left">
                        <span className="block text-sm font-medium text-gray-900 capitalize">{level}</span>
                        {(isRecommended || isCurrent) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            {isRecommended ? 'Recommended' : 'Current Level'}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Explore Subtopics Section */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl sm:p-8 p-4 shadow-md border border-indigo-200 mb-4 sm:mb-8">
            <div className="text-center mb-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {performanceScore === 100 && lastQuizDifficulty === 'hard'
                  ? `Ready to Explore More ${topic} Topics?`
                  : `Deep Dive into ${topic}`}
              </h3>
              <p className="text-gray-600 text-sm">
                {performanceScore === 100 && lastQuizDifficulty === 'hard'
                  ? "You've mastered this topic! Explore these related areas"
                  : `Explore specific aspects of ${topic}`}
              </p>
            </div>

            {isLoadingSubtopics ? (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
              </div>
            ) : subtopics && subtopics.length > 0 ? (
              <div>
                <div className="flex flex-wrap gap-1.5">
                  {visibleSubtopics.map((subtopic, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedTopic(subtopic);
                        setDifficulty('easy');
                        setPhase('customize');
                      }}
                      className="px-2.5 py-0.5 bg-white rounded-full border border-gray-200 
                               hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-200 
                               text-gray-700 hover:text-indigo-600 text-sm
                               shadow-sm hover:shadow active:bg-indigo-100 whitespace-nowrap
                               sm:max-w-none max-w-[calc(100vw-4rem)] overflow-hidden"
                      title={subtopic}
                    >
                      <span className="block truncate">{subtopic}</span>
                    </button>
                  ))}
                </div>
                
                {subtopics.length > 10 && (
                  <div className="flex justify-center mt-2">
                    <button
                      onClick={() => setShowAllSubtopics(!showAllSubtopics)}
                      className="text-xs text-indigo-600 hover:text-indigo-800 
                               flex items-center gap-1 px-2 py-1 rounded-full
                               hover:bg-indigo-50 transition-all duration-200"
                    >
                      {showAllSubtopics ? 'Show Less' : `Show ${subtopics.length - 10} More`}
                      <svg className={`w-3 h-3 transform transition-transform duration-200 
                                    ${showAllSubtopics ? 'rotate-180' : ''}`} 
                           fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                )}

                <div className="text-center text-xs text-gray-400 mt-1">
                  Showing {visibleSubtopics.length} of {subtopics.length} subtopics
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500">No subtopics available for this topic.</div>
            )}
          </div>

          {/* Create New Quiz Section */}
          <div className="bg-white rounded-xl sm:p-6 p-4 shadow-sm border border-gray-200">
            <div className="text-center space-y-2">
              <h3 className="text-base font-medium text-gray-900">
                {performanceScore === 100 && lastQuizDifficulty === 'hard'
                  ? "Ready for a New Challenge?"
                  : "Try a Different Topic?"}
              </h3>
              <button
                onClick={() => setPhase('topic')}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 text-gray-700
                         rounded-lg hover:bg-gray-50 transition-all duration-200 
                         shadow-sm hover:shadow-md flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create a New Quiz
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleFeedbackSubmit = async () => {
    // Reset states
    setFeedbackError('');
    
    // Validate feedback
    if (!feedbackText.trim()) {
      setFeedbackError('Feedback is required');
      return;
    }
    
    // Validate email if provided
    if (feedbackEmail && !isValidEmail(feedbackEmail)) {
      setFeedbackError('Please enter a valid email address');
      return;
    }
    
    try {
      setIsSubmittingFeedback(true);
      
      const payload = {
        feedback: feedbackText,
        email_id: feedbackEmail || undefined
      };
      
      const response = await fetch('https://quizcracker-backend.vercel.app/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }
      
      // Success
      setFeedbackSuccess(true);
      setFeedbackText('');
      setFeedbackEmail('');
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setFeedbackSuccess(false);
      }, 3000);
      
    } catch (err) {
      console.error('Feedback submission error:', err);
      setFeedbackError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Add a function to reset difficulty to easy when creating a new quiz
  const handleCreateNewQuiz = () => {
    // Reset difficulty to easy for new quizzes
    setQuizSettings(prev => ({
      ...prev,
      difficulty: ['easy']
    }));
    setLastQuizDifficulty(''); // Clear the last quiz difficulty
    setPhase('topic');
  };

  // Update the handleNext function to reset currentQuestionIndex when entering review mode
  useEffect(() => {
    if (phase === 'review') {
      setCurrentQuestionIndex(0); // Reset to first question when entering review mode
    }
  }, [phase]);

  return (
    <div className="min-h-screen flex flex-col">
      <div className={`border-b ${phase === 'quiz' || phase === 'review' ? 'hidden sm:block' : 'block'}`}>
        <Header />
      </div>

      <div className="flex-1 bg-gray-50">
        <div className="max-w-3xl mx-auto px-2 sm:px-6 pb-16 sm:pb-20">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-5 py-4 rounded-lg relative mb-8 text-lg">
              {error}
              <button
                className="absolute top-0 right-0 p-4"
                onClick={() => setError(null)}
              >
                ×
              </button>
            </div>
          )}
          
          {phase === 'topic' && renderTopicPhase()}
          {phase === 'customize' && renderCustomizePhase()}
          {phase === 'generating' && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Generating your personalized quiz...</p>
            </div>
          )}
          {(phase === 'quiz' || phase === 'review') && renderQuizPhase()}
          {phase === 'summary' && renderSummaryPhase()}
        </div>
      </div>

      {/* Only show footer when not in quiz or review phase on mobile */}
      <div className={`mt-auto ${(phase === 'quiz' || phase === 'review') ? 'hidden sm:block' : 'block'}`}>
        <Footer />
      </div>
    </div>
  );
};

export default QuizDisplay;